import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { API, authFetch } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import PortalLayout from "../components/PortalLayout";
import {
  Users,
  User,
  FileText,
  BookOpen,
  Eye,
  MessageSquare,
  Clock,
  AlertCircle,
  CheckCircle,
  Search,
  ChevronRight,
  IndianRupee,
  ListTodo,
  Upload,
  Activity,
  UserPlus,
} from "lucide-react";


const TeamDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, clientsRes] = await Promise.all([
        authFetch(`${API}/team/dashboard-stats`),
        authFetch(`${API}/team/my-clients`),
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (clientsRes.ok) setClients(await clientsRes.json());
    } catch (error) {
      console.error("Error fetching team data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredClients = clients.filter(
    (c) =>
      c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg-primary)" }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <PortalLayout title="Team Dashboard">

      <div className="relative z-10 max-w-7xl mx-auto px-3 sm:px-6 py-6 sm:py-8">
        {/* Welcome */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-3">
          <h1
            className="font-['Playfair_Display'] text-3xl font-bold mb-1"
            style={{ color: "var(--text-primary)" }}
          >
            Welcome, {user?.name?.split(" ")[0]}
          </h1>
          <p style={{ color: "var(--text-secondary)" }}>
            Here's a summary of your assigned clients and tasks.
          </p>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-3"
        >
          {[
            { label: "My Clients", value: stats?.total_clients || 0, icon: Users, color: "var(--accent)" },
            { label: "Total Documents", value: stats?.total_documents || 0, icon: FileText, color: "#3B82F6" },
            { label: "Transactions", value: stats?.total_transactions || 0, icon: BookOpen, color: "#F59E0B" },
            { label: "Unread Messages", value: stats?.unread_messages || 0, icon: MessageSquare, color: "#8B5CF6" },
            { label: "Pending Tasks", value: stats?.pending_tasks || 0, icon: ListTodo, color: stats?.pending_tasks > 0 ? "#EF4444" : "#10B981" },
          ].map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 + i * 0.03 }}
              className="soft-card"
              data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${stat.color}15` }}>
                  <stat.icon className="w-5 h-5" style={{ color: stat.color }} />
                </div>
                <div>
                  <p className="text-xs" style={{ color: "var(--text-muted)" }}>{stat.label}</p>
                  <p className="text-xl font-bold" style={{ color: "var(--text-primary)" }}>{stat.value}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Quick Actions */}

        {/* My Clients */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="soft-card"
          data-testid="my-clients-section"
        >
          <div className="flex items-center justify-between mb-3">
            <h2
              className="font-['Playfair_Display'] text-xl font-bold text-[var(--text-main)]"
            >
              My Clients
            </h2>
            {clients.length > 3 && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--text-muted)" }} />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search clients..."
                  className="pl-9 w-64 rounded-xl border-[var(--border-light)] bg-white"
                  data-testid="search-clients-input"
                />
              </div>
            )}
          </div>

          {filteredClients.length > 0 ? (
            <div className="space-y-3">
              {filteredClients.map((client) => {
                const svc = client.payment?.service_type;
                return (
                  <div
                    key={client.user_id}
                    className="p-5 rounded-[20px] border border-[var(--border-light)] bg-white shadow-sm cursor-pointer hover:border-[var(--primary)] hover:shadow-md transition-all group"
                    onClick={() => navigate(`/team/client/${client.user_id}`)}
                    data-testid={`client-card-${client.user_id}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex items-center gap-4 min-w-0">
                        {client.picture ? (
                          <img src={client.picture} alt={client.name} className="w-12 h-12 rounded-full object-cover shadow-sm border border-[var(--border-light)] ring-2 ring-transparent group-hover:ring-[var(--primary)] transition-all shrink-0" />
                        ) : (
                          <div className="w-12 h-12 rounded-full flex items-center justify-center shadow-sm border border-[var(--border-light)] bg-[var(--primary)]/10 shrink-0">
                            <User className="w-5 h-5 text-[var(--primary)]" />
                          </div>
                        )}
                        <div className="min-w-0 overflow-hidden">
                          <p className="font-semibold truncate" style={{ color: "var(--text-primary)" }}>{client.name}</p>
                          <p className="text-sm truncate" style={{ color: "var(--text-secondary)" }}>{client.email}</p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between sm:justify-end gap-5 w-full sm:w-auto mt-1 sm:mt-0">
                        {svc && (
                          <span className="px-3 py-1 text-xs font-bold rounded-full bg-[var(--primary)]/10 text-[var(--primary)] whitespace-nowrap">
                            {svc === "itr_filing" ? "ITR" : svc === "accounting" ? "Accounting" : "Combined"}
                          </span>
                        )}
                        <div className="hidden md:flex items-center gap-4 text-sm" style={{ color: "var(--text-muted)" }}>
                          <span className="flex items-center gap-1">
                            <FileText className="w-3.5 h-3.5" /> {client.document_count}
                          </span>
                          <span className="flex items-center gap-1">
                            <BookOpen className="w-3.5 h-3.5" /> {client.transaction_count}
                          </span>
                        </div>
                        <ChevronRight className="w-5 h-5 text-[var(--accent)] opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity ml-auto sm:ml-0" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto mb-3" style={{ color: "var(--text-muted)" }} />
              <h3 className="text-lg font-bold" style={{ color: "var(--text-primary)" }}>No clients found</h3>
              <p style={{ color: "var(--text-secondary)" }}>Adjust your search or check back later.</p>
            </div>
          )}
        </motion.div>
      </div>
    </PortalLayout>
  );
};

export default TeamDashboard;
